import { useTranslation } from 'react-i18next';
import { Layer, LayerProps, Source } from 'react-map-gl';

import { CONSERVATION_MAP_URL, Threat, Tileset } from '@/api/rest/resources/conservation';
import { useConservationProjectName } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useConservationProjectName';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useThreatGeojson } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useThreatGeojson';
import { useTilesetsByLayer } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useTilesets';

import { COLORS } from '../constants';
import { useControlsContext } from '../hooks/useControlsForm';
import { useMoveInteractiveLayersToTop } from '../hooks/useInteractiveLayers';
import { BinaryLegend, IndexLegend, Legend } from './Legend';

export const HabitatThreatLayers = () => {
  const controlsForm = useControlsContext();

  const [threatFilters] = controlsForm.watch(['threatFilters']);

  const showCombined = !threatFilters || threatFilters.length === 0;

  return showCombined ? <CombinedLayer /> : <ThreatLayers />;
};

const COMBINED_LAYER_SOURCE_ID = 'combined-layer-source';
const COMBINED_LAYER_FILL_LAYER_ID = 'combined-layer-fill-layer';
const THREAT_LAYER_SOURCE_ID = 'threat-layer-source';

const CombinedLayer = () => {
  const { t } = useTranslation();

  const controlsForm = useControlsContext();

  const projectName = useConservationProjectName();
  const projectUrlSource = projectName ? CONSERVATION_MAP_URL[`${projectName}`] : '';

  const habitatIntactnessLegendColorsRange = useFact('r1_conservation_habitat_intactness_legend_color_range')?.value as
    | { from?: string; to?: string }
    | undefined;

  const legendColors = {
    from: habitatIntactnessLegendColorsRange?.from || '#F8EDB7',
    to: habitatIntactnessLegendColorsRange?.to || '#8B391E',
  };

  const [year] = controlsForm.watch(['year']);

  const tilesetsByLayer = useTilesetsByLayer();

  // eslint-disable-next-line security/detect-object-injection
  const tileset = tilesetsByLayer?.combined?.[year];

  useMoveInteractiveLayersToTop(COMBINED_LAYER_SOURCE_ID);

  if (!tileset) {
    return null;
  }

  const RasterLayer = (
    <Source
      key={tileset.id} // Force load tilesets
      id={COMBINED_LAYER_SOURCE_ID}
      type='raster'
      tileSize={256}
      tiles={[`${projectUrlSource}${tileset.id}`]}
    >
      <Layer
        id={COMBINED_LAYER_FILL_LAYER_ID}
        type='raster'
        paint={{
          'raster-opacity': 0.8,
          'raster-saturation': 0,
          'raster-contrast': 0.1,
        }}
      />
    </Source>
  );

  const VectorLayer = (
    <Source
      key={tileset.id} // Force load tilesets
      id={COMBINED_LAYER_SOURCE_ID}
      type='vector'
      url={`${projectUrlSource}${tileset.id}`}
    >
      <Layer
        id={COMBINED_LAYER_FILL_LAYER_ID}
        type='fill'
        source={COMBINED_LAYER_SOURCE_ID}
        source-layer={tileset.sourceLayer}
        paint={{
          'fill-color': [
            'interpolate',
            ['linear'],
            ['get', 'value'],
            0.0,
            COLORS.combined.index[0],
            1.0,
            COLORS.combined.index[1],
          ],
          'fill-opacity': 0.4,
        }}
      />
    </Source>
  );

  return (
    <>
      {tileset?.type === 'raster' ? RasterLayer : VectorLayer}
      <Legend>
        <IndexLegend
          // eslint-disable-next-line sonarjs/no-duplicate-string
          title={t('shared.projects.project.conservation.interactiveMap.labels.habitatIntactness')}
          infoPopoverProps={{
            title: t('shared.projects.project.conservation.interactiveMap.labels.habitatIntactness'),
            body: t('shared.projects.project.conservation.interactiveMap.explainers.habitatIntactness'),
          }}
          range={[
            {
              value: 0,
              color: legendColors.from,
            },
            {
              value: 1,
              color: legendColors.to,
            },
          ]}
        />
      </Legend>
    </>
  );
};

const ThreatLayers = () => {
  const { t } = useTranslation();

  const controlsForm = useControlsContext();

  const [threatFilters] = controlsForm.watch(['threatFilters']);

  if (!threatFilters) {
    return null;
  }

  return (
    <>
      {threatFilters.map((threat) => (
        <ThreatLayer key={threat} threat={threat} />
      ))}

      <Legend>
        <BinaryLegend
          title={t('shared.projects.project.conservation.interactiveMap.labels.habitatIntactness')}
          layers={threatFilters.map((threat) => ({
            label: t(`shared.projects.project.conservation.labels.${threat}`),
            // eslint-disable-next-line security/detect-object-injection, @typescript-eslint/no-explicit-any
            color: (COLORS as any)[threat].binary as string,
          }))}
          infoPopoverProps={{
            title: t('shared.projects.project.conservation.interactiveMap.labels.habitatIntactness'),
            body: t('shared.projects.project.conservation.interactiveMap.explainers.habitatIntactness'),
          }}
        />
      </Legend>
    </>
  );
};

const ThreatLayer = ({ threat }: { threat: Threat }) => {
  const threatsWithGenericLayer = [
    Threat.built,
    Threat.crop,
    Threat.treeloss,
    Threat.mine,
    Threat.overgrazing,
    Threat.major_road,
    Threat.minor_road,
    Threat.fire,
    Threat.river,
  ];
  const threatsWithCircleGeojsonLayer = [Threat.airstripes];

  useMoveInteractiveLayersToTop(threat);

  if (threat === Threat.road) {
    return <RoadsLayer />;
  }

  if (threatsWithCircleGeojsonLayer.includes(threat)) {
    return <GeojsonCircleLayer threat={threat} />;
  }

  if (threatsWithGenericLayer.includes(threat)) {
    return <ThreatMapLayer threat={threat} />;
  }

  return null;
};

const RoadsLayer = () => {
  const threat = Threat.road;
  const tileset = useTileset(threat);

  const projectName = useConservationProjectName();
  const projectUrlSource = projectName ? CONSERVATION_MAP_URL[`${projectName}`] : '';

  if (!tileset) {
    return null;
  }

  if (tileset?.type === 'raster') {
    return <RasterSourceLayer tileset={tileset} threat={threat} projectUrlSource={projectUrlSource} />;
  }

  return <VectorSourceLayer tileset={tileset} threat={threat} projectUrlSource={projectUrlSource} type='line' />;
};

const GeojsonCircleLayer = ({ threat }: { threat: Threat }) => {
  const threatGeojson = useThreatGeojson(threat);
  const controlsForm = useControlsContext();

  const [year] = controlsForm.watch(['year']);

  if (!threatGeojson) {
    return null;
  }

  return (
    <Source key={year} id={THREAT_LAYER_SOURCE_ID} type='geojson' data={threatGeojson}>
      <Layer
        id='threat-layer-circle-layer'
        type='circle'
        source={THREAT_LAYER_SOURCE_ID}
        paint={{
          'circle-color': COLORS.airstripes.binary,
          'circle-opacity': 1,
          'circle-stroke-width': 1,
          'circle-stroke-color': 'rgb(255, 255, 255)',
          'circle-stroke-opacity': 1,
        }}
      />
    </Source>
  );
};

type FillLayerProps = {
  threat: Threat;
};

const ThreatMapLayer = ({ threat }: FillLayerProps) => {
  const tileset = useTileset(threat);
  const projectName = useConservationProjectName();
  const projectUrlSource = projectName ? CONSERVATION_MAP_URL[`${projectName}`] : '';

  if (!tileset) {
    return null;
  }

  if (tileset?.type === 'raster') {
    return <RasterSourceLayer tileset={tileset} threat={threat} projectUrlSource={projectUrlSource} />;
  }

  return <VectorSourceLayer tileset={tileset} threat={threat} projectUrlSource={projectUrlSource} type='fill' />;
};

const useTileset = (threat: Threat) => {
  const controlsForm = useControlsContext();

  const [year] = controlsForm.watch(['year']);

  const tilesetsByLayer = useTilesetsByLayer();

  // eslint-disable-next-line security/detect-object-injection
  return tilesetsByLayer?.[threat]?.[year];
};

type SourceLayerProps = {
  tileset: Tileset;
  threat: Threat;
  projectUrlSource: string;
};

const RasterSourceLayer = ({ tileset, threat, projectUrlSource }: SourceLayerProps) => {
  return (
    <Source
      key={tileset.id} // Force load tilesets
      id={threat}
      type='raster'
      tileSize={256}
      tiles={[`${projectUrlSource}${tileset.id}`]}
    >
      <Layer
        id={`feat-${threat}-fill`}
        type='raster'
        paint={{
          'raster-opacity': 1,
        }}
      />
    </Source>
  );
};

const VectorSourceLayer = ({
  tileset,
  threat,
  projectUrlSource,
  type = 'fill',
}: SourceLayerProps & {
  type: 'fill' | 'line';
}) => {
  const layerOptions: LayerProps =
    type === 'fill'
      ? {
          type,
          paint: {
            'fill-color': COLORS[`${threat}`].binary,
            'fill-opacity': ['case', ['==', ['get', 'value'], 1], 0.8, 0.0],
          },
        }
      : {
          type,
          paint: {
            'line-width': 2,
            'line-color': COLORS[`${threat}`].binary,
          },
        };

  return (
    <Source
      key={tileset.id} // Force load tilesets
      id={threat}
      type='vector'
      url={`${projectUrlSource}${tileset.id}`}
    >
      <Layer id={`feat-${threat}-fill`} source={threat} source-layer={tileset.sourceLayer} {...layerOptions} />
    </Source>
  );
};
